import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { DESTINATIONS, type DestinationId } from "@/lib/destinations";

const LINK = "rounded-[8px] px-3 py-2 text-sm whitespace-nowrap";
const LINK_CURRENT = `${LINK} font-semibold text-brand-fg`;
const LINK_IDLE = `${LINK} font-medium text-body hover:text-ink`;

/**
 * The five destinations in the header, from `lg`, each by its long label and
 * packed to the start; the active destination is the current page, and with
 * no active destination no link is. Below `xl` the nav is the header's second
 * row, drawn after every part of the brand row at the row's full width; from
 * `xl` it sits in the one row after the utility's name and takes the row's
 * spare width. It never wraps a label, so it is never what gives way.
 */
export function TopNav({ active }: { active: DestinationId | null }) {
  const { t } = useTranslation("customer");
  return (
    <nav
      aria-label={t("shell.primaryNav")}
      className="hidden lg:order-last lg:flex lg:basis-full xl:order-none xl:flex-1"
    >
      <ul className="flex items-center gap-1">
        {DESTINATIONS.map((destination) => {
          const current = destination.id === active;
          return (
            <li key={destination.id}>
              {/* The router marks a link matching the location as the current
                  page by itself; exact matching leaves the mark to `active`. */}
              <Link
                to={destination.path}
                activeOptions={{ exact: true }}
                aria-current={current ? "page" : undefined}
                className={current ? LINK_CURRENT : LINK_IDLE}
              >
                {t(destination.labelKey)}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
